<template>
  <!-- 팔로워 상세 정보 표시 전용 컴포넌트 — FollowerList.vue 모달과 FollowerLookupDrawer.vue 양쪽에서 재사용.
       조회(fetch)는 하지 않고 전달받은 data 를 그대로 렌더링만 한다. -->
  <div v-if="data" class="detail-container">
    <div class="detail-content">
      <!-- 기본 정보 섹션 -->
      <div class="detail-section">
        <h3 class="section-title">
          <i class="fas fa-user-circle"></i>
          {{ $t('followerlist_basic_info') }}
        </h3>
        <a-descriptions bordered :column="compact ? 1 : undefined">
          <a-descriptions-item :label="$t('followerlist_name')" :span="compact ? 1 : 2">
            {{ data.name || data.nickname || '-' }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('followerlist_phone')" :span="1">
            {{ formatPhoneNumber(data.mobile) }}
          </a-descriptions-item>
        </a-descriptions>
      </div>

      <!-- 계정 정보 섹션 -->
      <div class="detail-section">
        <h3 class="section-title">
          <i class="fas fa-id-card"></i>
          {{ $t('followerlist_account_info') }}
        </h3>
        <a-descriptions bordered :column="compact ? 1 : undefined">
          <a-descriptions-item :label="$t('followerlist_follow_date')" :span="compact ? 1 : 2">
            {{ formatDate(data.createdAt) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('followerlist_follow_source')" :span="1">
            <a-tooltip :title="$t('followerlist_view_wechat_doc')">
              <span>{{ data.subscribe_scene || $t('followerlist_unknown_source') }}</span>
              <i
                class="fas fa-info-circle"
                style="color: #1890ff; cursor: pointer; font-size: 14px; margin-left: 4px;"
                @click="openWechatDoc"
              />
            </a-tooltip>
          </a-descriptions-item>
        </a-descriptions>
      </div>

      <!-- 카드 목록 섹션 -->
      <div class="detail-section" v-if="data.cardInfos?.length">
        <h3 class="section-title">
          <i class="fas fa-credit-card"></i>
          {{ $t('followerlist_card_list') }}
        </h3>
        <a-table
          :columns="cardColumns"
          :data-source="data.cardInfos"
          size="small"
          :pagination="false"
          :scroll="compact ? { x: 520 } : undefined"
          class="card-table"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import { GZH_LABEL_MAP } from '../gzh-constants';

// gzhName(논리명) → 카드 이미지 자산 매핑. 카드를 실물 운영하는 gzh만 등록한다
// (현재는 덴티움만). 매핑에 없는 gzhName은 카드 이미지 컬럼에 '-'로 표시된다.
// FollowerList.vue 의 동일 매핑을 그대로 옮겨왔다.
const CARD_IMAGE_ASSET_MAP = {
  'dentium-china-dyh': () => require('@/assets/images/cards/dentium-warranty-card.png'),
};

export default defineComponent({
  name: 'FollowerDetailPanel',
  props: {
    // 팔로워 상세 데이터 — null 이면 아무것도 렌더링하지 않는다 (조회는 상위 컴포넌트 책임)
    data: {
      type: Object,
      default: null
    },
    // 좁은 폭(드로어 420px)에서 호출될 때 true — descriptions 를 1열로 접고 카드 표에 가로 스크롤을 준다.
    // 기본 false 라 기존 소비처(FollowerList 모달 800px)의 렌더링은 그대로 유지된다.
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cardImageMap: {},
      cardColumns: [
        {
          title: this.$t('followerlist_card_image'),
          dataIndex: 'cardImage',
          key: 'cardImage',
          align: 'center',
          customRender: ({ record }) => {
            const imageUrl = this.cardImageMap[record.gzhName];
            if (!imageUrl) return '-';
            return (
              <div style="display: flex; justify-content: center;">
                <img
                  src={imageUrl}
                  alt={this.$t('followerlist_genuine_warranty')}
                  style="height: 40px; object-fit: contain;"
                  onError={(e) => {
                    console.error('Card image loading failed');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            );
          }
        },
        {
          title: this.$t('followerlist_gzh_name'),
          dataIndex: 'gzhName',
          key: 'gzhName',
          customRender: ({ text }) => this.formatGzhName(text)
        },
        {
          title: this.$t('followerlist_card_title'),
          dataIndex: 'cardTitle',
          key: 'cardTitle'
        },
        {
          title: this.$t('followerlist_card_id'),
          dataIndex: 'cardId',
          key: 'cardId',
          width: 200,
          customRender: ({ text }) => (
            <div style="display: flex; align-items: center; justify-content: space-between">
              <span style="font-family: monospace; color: #666">{text}</span>
              <a-button
                type="link"
                size="mini"
                style="padding: 0 4px; min-width: auto"
                onClick={() => this.copyToClipboard(text, '卡片ID')}
              >
                <i class="fas fa-copy" />
              </a-button>
            </div>
          )
        },
        {
          title: this.$t('followerlist_card_code'),
          dataIndex: 'cardCode',
          key: 'cardCode',
          width: 120,
          customRender: ({ text }) => (
            <div style="display: flex; align-items: center; justify-content: space-between">
              <span style="font-family: monospace; color: #666">{this.formatCardCode(text)}</span>
              <a-button
                type="link"
                size="mini"
                style="padding: 0 4px; min-width: auto"
                onClick={() => this.copyToClipboard(text.replace(/\s/g, ''), this.$t('followerlist_card_code'))}
              >
                <i class="fas fa-copy" />
              </a-button>
            </div>
          )
        }
      ]
    };
  },
  created() {
    // gzh별 카드 이미지 자산 로드 — 매핑에 등록된 gzh만 시도(현재 덴티움만)
    this.cardImageMap = Object.entries(CARD_IMAGE_ASSET_MAP).reduce((acc, [gzhName, loadAsset]) => {
      try {
        acc[gzhName] = loadAsset();
      } catch (error) {
        console.error(`Failed to load card image for ${gzhName}:`, error);
      }
      return acc;
    }, {});
  },
  methods: {
    // 날짜 포맷팅
    formatDate(date) {
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    },
    // 전화번호 포맷팅 함수 — 중국 휴대폰(11자리, 3-4-4)을 기본으로 다룬다.
    // 포맷 조건에 맞지 않으면(자릿수 상이 등) 원문을 그대로 반환한다(포맷 실패 시 원문 노출).
    formatPhoneNumber(phone) {
      if (!phone) return '-';
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
      return phone;
    },
    // 클립보드 복사 메서드
    async copyToClipboard(text, type) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success(this.$t('followerlist_copy_success', { type }));
      } catch (err) {
        console.error('Failed to copy:', err);
        this.$message.error(this.$t('followerlist_copy_fail', { type }));
      }
    },
    // 공중호 이름 포맷팅 — 공용 GZH_LABEL_MAP 사용(FollowerStats.vue와 동일 규칙), 미매핑 시 원본 반환
    formatGzhName(gzhName) {
      return GZH_LABEL_MAP[gzhName] || gzhName;
    },
    // 카드 코드 포맷팅 (4자리마다 공백 추가)
    formatCardCode(code) {
      if (!code) return '-';
      return code.replace(/(.{4})/g, '$1 ').trim();
    },
    // 공식 문서 링크 오픈 메서드
    openWechatDoc() {
      const url = 'https://developers.weixin.qq.com/doc/offiaccount/User_Management/Get_users_basic_information_UnionID.html#%E8%BF%94%E5%9B%9E%E8%AF%B4%E6%98%8E';
      if (window) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.detail-container {
  .detail-content {
    padding: 24px;
  }

  .detail-section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #1f2937;
      display: flex;
      align-items: center;

      i {
        margin-right: 8px;
        color: #3b82f6;
      }
    }
  }

  :deep(.ant-descriptions) {
    background-color: #fff;

    .ant-descriptions-item-label {
      width: 120px;
      background-color: #f8f9fa;
      color: #4b5563;
      font-weight: 500;
    }

    .ant-descriptions-item-content {
      color: #1f2937;
    }
  }

  .card-table {
    :deep(.ant-table) {
      border-radius: 8px;

      .ant-table-thead > tr > th {
        background-color: #f8f9fa;
        color: #4b5563;
        font-weight: 600;
      }

      .ant-table-tbody > tr > td {
        vertical-align: middle;
      }
    }
  }
}
</style>
